import { Policy, PolicyStatus, CloudPolicyType } from '../../types/policies';
import { Card, CardContent, CardHeader } from '../../../../ui/common-ui/card';
import { Badge } from '../../../../ui/common-ui/badge';
import { Button } from '../../../../ui/common-ui/button';
import { Switch } from '../../../../ui/common-ui/switch';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldOff,
  ChevronRight,
  Zap,
  Link2,
  Activity,
  User,
  Database,
  Wrench,
  Loader2,
  Play,
} from 'lucide-react';
import { getPolicyCommands, normalizeExecutionHistory, normalizePolicySeverity } from '../../lib/policyTransforms';

interface PolicyCardProps {
  policy: Policy;
  onViewDetails: (policy: Policy) => void;
  onToggle: (id: string | number, enabled: boolean) => void;
  showToggle?: boolean;
}

const getSeverityConfig = (severity: string) => {
  const configs: Record<string, { className: string; borderColor: string; dotColor: string; label: string }> = {
    Critical: {
      className: 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400',
      borderColor: 'border-l-red-500',
      dotColor: 'bg-red-500',
      label: 'Critical',
    },
    High: {
      className: 'bg-orange-500/10 border-orange-500/30 text-orange-600 dark:text-orange-400',
      borderColor: 'border-l-orange-500',
      dotColor: 'bg-orange-500',
      label: 'High',
    },
    Medium: {
      className: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-600 dark:text-yellow-400',
      borderColor: 'border-l-yellow-500',
      dotColor: 'bg-yellow-500',
      label: 'Medium',
    },
    Low: {
      className: 'bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400',
      borderColor: 'border-l-green-500',
      dotColor: 'bg-green-500',
      label: 'Low',
    },
  };
  return configs[severity] || configs.Low;
};

const getCategoryConfig = (category: CloudPolicyType) => {
  const configs: Record<CloudPolicyType, { icon: any; label: string; className: string }> = {
    'GUARDRAIL': {
      icon: ShieldCheck,
      label: 'Guardrail',
      className: 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400',
    },
    'REMEDIATION': {
      icon: Wrench,
      label: 'Remediation',
      className: 'bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400',
    },
  };
  return configs[category] || configs.GUARDRAIL;
};

const getStatusConfig = (status: PolicyStatus) => {
  const configs: Record<PolicyStatus, { icon: any; label: string; className: string }> = {
    ACTIVE: {
      icon: ShieldCheck,
      label: 'Active',
      className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
    },
    INACTIVE: {
      icon: ShieldOff,
      label: 'Inactive',
      className: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
    },
    ENABLING: {
      icon: Loader2,
      label: 'Enabling',
      className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
    },
    DISABLING: {
      icon: Loader2,
      label: 'Disabling',
      className: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
    },
  };
  return configs[status] || configs.INACTIVE;
};

const isAllTargets = (targets?: string[]) => (
  Array.isArray(targets) && targets.some(target => target === '' || target === '*')
);

const getTargetSummary = (policy: Policy) => {
  if (isAllTargets(policy.includedUsers) || isAllTargets(policy.includedNHIs) || isAllTargets(policy.includedResources) ||
      isAllTargets(policy.excludedUsers) || isAllTargets(policy.excludedNHIs) || isAllTargets(policy.excludedResources)) {
    return 'All targets';
  }
  const count = (policy.includedUsers?.length || 0) + (policy.includedNHIs?.length || 0) + (policy.includedResources?.length || 0) +
                (policy.excludedUsers?.length || 0) + (policy.excludedNHIs?.length || 0) + (policy.excludedResources?.length || 0);
  return count > 0 ? `${count} target${count === 1 ? '' : 's'}` : (policy.policyTargetTypes?.join(', ') || 'No target');
};

export const PolicyCard = ({ policy, onViewDetails, onToggle, showToggle = true }: PolicyCardProps) => {
  const severityConfig = getSeverityConfig(normalizePolicySeverity(policy.severity));
  const categoryConfig = getCategoryConfig(policy.cloudPolicyType);
  const statusConfig = getStatusConfig(policy.policyStatus);
  const CategoryIcon = categoryConfig.icon;
  const StatusIcon = statusConfig.icon;

  const execHistory = normalizeExecutionHistory(policy.executionHistory);
  const execCount = execHistory.length;
  const uniqueEntities = new Set(execHistory.map((e: any) => e.targetEntity)).size;
  const entityUsers = new Set(execHistory.filter((e: any) => e.entityType === 'user' || e.entityType === 'service-account').map((e: any) => e.targetEntity)).size;
  const entityResources = new Set(execHistory.filter((e: any) => e.entityType === 'resource' || e.entityType === 'role').map((e: any) => e.targetEntity)).size;
  const commands = getPolicyCommands(policy.policyEnableData);
  const linkedAlerts = Array.isArray(policy.linkedAlerts) ? policy.linkedAlerts : [];
  const actionLabel = commands[0]?.operation || commands[0]?.type || commands[0]?.sdkAction || 'Configured action';
  const isStatusTransitioning = policy.policyStatus === 'ENABLING' || policy.policyStatus === 'DISABLING';

  return (
    <Card
      className={`
        group relative bg-card overflow-hidden rounded-2xl border border-border/50 border-l-4 ${severityConfig.borderColor}
        shadow-lg shadow-sky-500/5 hover:shadow-[0_4px_16px_hsl(var(--primary)/0.06)]
        transition-all duration-300 ease-out hover:-translate-y-px hover:border-primary/30 hover:border-l-primary
      `}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`${categoryConfig.className} w-fit font-medium text-xs transition-transform group-hover:scale-105`}
            >
              <CategoryIcon className="h-3 w-3 mr-1" />
              {categoryConfig.label}
            </Badge>
            <Badge
              variant="outline"
              className={`${severityConfig.className} w-fit font-medium text-xs transition-transform group-hover:scale-105`}
            >
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${severityConfig.dotColor}`} />
              {severityConfig.label}
            </Badge>
            <Badge
              variant="outline"
              className={`${statusConfig.className} w-fit font-medium text-xs transition-transform group-hover:scale-105`}
            >
              <StatusIcon className={`h-3 w-3 mr-1 ${(policy.policyStatus === 'ENABLING' || policy.policyStatus === 'DISABLING') ? 'animate-spin' : ''}`} />
              {statusConfig.label}
            </Badge>
          </div>
          {showToggle && (
            <div className="relative flex items-center justify-end h-6 w-11">
              <div 
                className={`absolute inset-0 flex items-center justify-end transition-all duration-300 ease-in-out ${
                  isStatusTransitioning
                    ? 'opacity-100 scale-100 translate-x-0' 
                    : 'opacity-0 scale-75 translate-x-4 pointer-events-none'
                }`}
              >
                <Loader2 className="h-5 w-5 animate-spin text-primary mr-1" />
              </div>
              <div
                className={`transition-all duration-300 ease-in-out ${
                  isStatusTransitioning
                    ? 'opacity-0 scale-75 -translate-x-4 pointer-events-none'
                    : 'opacity-100 scale-100 translate-x-0'
                }`}
              >
                <Switch
                  checked={policy.policyStatus === 'ACTIVE'}
                  onCheckedChange={(checked) => policy.id !== undefined && onToggle(policy.id, checked)}
                  disabled={isStatusTransitioning}
                  className="data-[state=checked]:bg-primary transition-all duration-200"
                />
              </div>
            </div>
          )}
        </div>

        <h3 className="font-semibold text-base text-foreground leading-tight mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-200">
          {policy.title}
        </h3>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <ShieldAlert className="h-3 w-3" />
            <span>{policy.cloudType}</span>
          </div>
          {policy.policyTargetTypes && policy.policyTargetTypes.length > 0 && (
            <>
              <span className="text-border">/</span>
              <div className="flex flex-wrap items-center gap-1">
                {policy.policyTargetTypes.map((type, idx) => (
                  <span key={idx} className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[10px] font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                    {type}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pt-0">
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
          {policy.description}
        </p>

        {commands.length > 0 && (
          <div className="bg-muted/40 rounded-2xl p-3 space-y-2 border border-border/50 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                Auto Action
              </span>
              <Badge variant="secondary" className="text-xs h-5">
                {commands.length} {commands.length === 1 ? 'Step' : 'Steps'}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 text-xs">
              <span className="font-mono bg-background px-2 py-0.5 rounded-lg border border-border/50 truncate max-w-[230px]">
                {String(actionLabel).split('.').pop()}
              </span>
              {commands.length > 2 && (
                <span className="font-semibold text-muted-foreground px-1">
                  +{commands.length - 2} more
                </span>
              )}
            </div>
          </div>
        )}

        {execCount > 0 && (
          <div className="flex items-center gap-3 rounded-2xl border border-border/50 bg-muted/30 px-3 py-2 shadow-sm">
            <div className="flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold text-foreground">{execCount}</span>
              <span className="text-[11px] text-muted-foreground">executions</span>
            </div>
            <span className="text-border">│</span>
            {entityUsers > 0 && (
              <div className="flex items-center gap-1">
                <User className="h-3 w-3 text-blue-500" />
                <span className="text-xs font-medium text-foreground">{entityUsers}</span>
              </div>
            )}
            {entityResources > 0 && (
              <div className="flex items-center gap-1">
                <Database className="h-3 w-3 text-emerald-500" />
                <span className="text-xs font-medium text-foreground">{entityResources}</span>
              </div>
            )}
            <span className="text-[11px] text-muted-foreground">
              {uniqueEntities} {uniqueEntities === 1 ? 'entity' : 'entities'}
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary" className="text-xs h-5 bg-muted/60">
            {getTargetSummary(policy)}
          </Badge>
          {linkedAlerts.length > 0 && (
            <Badge variant="secondary" className="text-xs h-5 bg-muted/60">
              {linkedAlerts.length} alert{linkedAlerts.length > 1 ? 's' : ''}
            </Badge>
          )}
          {execCount > 0 && (
            <Badge variant="outline" className="text-xs h-5">
              {execCount} triggers
            </Badge>
          )}
        </div>

        <div className="hidden items-center justify-between border-t border-border/60 pt-3 text-xs text-muted-foreground">
          <div className="flex min-h-4 items-center gap-3">
            {linkedAlerts.length > 0 && (
              <div className="flex items-center gap-1">
                <Link2 className="h-3 w-3" />
                <span>{linkedAlerts.length} alert{linkedAlerts.length > 1 ? 's' : ''}</span>
              </div>
            )}
            {execCount > 0 && (
              <div className="flex items-center gap-1">
                <Zap className="h-3 w-3 text-amber-500" />
                <span>{execCount} triggers</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <Button
            onClick={() => onViewDetails(policy)}
            variant="outline"
            className={`${showToggle ? 'flex-1' : 'w-full'} rounded-2xl group/btn border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200`}
          >
            <span>View Details / Edit</span>
            <ChevronRight className="h-4 w-4 ml-auto transition-transform group-hover/btn:translate-x-1" />
          </Button>

          {showToggle && (
            <Button
              onClick={() => policy.id && onToggle(policy.id, true)}
              disabled={isStatusTransitioning}
              variant="default"
              className="rounded-2xl px-3 flex-shrink-0"
              title="Execute policy"
            >
              {isStatusTransitioning ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Execute
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
